/**
 * Admin → Users → (row click): a slide-over with the deep per-user
 * drill-down — billing/limit status, the three reconciled turn counts
 * (LLM calls vs metered vs library-served), and the real LLM cost
 * (today / this month / all-time + per-call avg + a rate-bucket breakdown).
 *
 * Cost is computed on demand for this one user (a bounded conversation scan),
 * which is why the list view doesn't show it for everyone.
 */

import { useQuery } from "@tanstack/react-query";

import { getUserDetail, type UserDetail } from "@/features/admin/lib/adminApi";
import {
  formatTokens,
  formatUsd,
  limitStateLabel,
} from "@/features/admin/lib/limits";
import { relativeTime } from "@/shared/format";

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

export function UserDetailPanel({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-user-detail", userId],
    queryFn: () => getUserDetail(userId),
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative w-full max-w-md h-full overflow-y-auto bg-surface border-l border-border p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold text-text break-all">
            {data?.email ?? userId}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-border px-3 py-1 text-sm text-text-muted hover:bg-text/5 hover:text-text transition"
          >
            Close
          </button>
        </div>

        {isLoading && <p className="text-sm text-text-muted">Loading…</p>}
        {isError && <p className="text-sm text-danger">Couldn't load user.</p>}
        {data && <DetailBody d={data} />}
      </div>
    </div>
  );
}

function DetailBody({ d }: { d: UserDetail }) {
  const tier =
    d.subscriptionStatus === "free" || !d.subscriptionStatus
      ? "Free"
      : `${d.subscriptionStatus}${d.planLevel ? ` · ${d.planLevel}` : ""}${d.plan ? ` (${d.plan})` : ""}`;

  return (
    <>
      <Section title="Billing & limits">
        <Row label="Tier" value={tier} />
        <Row label="Limit" value={limitStateLabel(d.limitState)} />
        {d.limitState === "metered" && d.monthlyCap != null && (
          <>
            <Row
              label="Used this month"
              value={`${d.responsesThisMonth} / ${d.monthlyCap}`}
            />
            <Row label="Cap resets" value={formatDate(d.capResetsAt)} />
          </>
        )}
        <Row label="Renews" value={formatDate(d.currentPeriodEnd)} />
        {d.isFounder && <Row label="Founder" value="yes" />}
        <Row label="Signals" value={String(d.signalCount)} />
        <Row label="XP" value={d.totalXp.toLocaleString()} />
        <Row label="Streak" value={d.dailyStreak > 0 ? `🔥 ${d.dailyStreak}` : "—"} />
        <Row label="Joined" value={formatDate(d.createdAt)} />
        <Row
          label="Last active"
          value={d.lastActiveAt ? relativeTime(d.lastActiveAt) : "never"}
        />
      </Section>

      <Section title="Activity">
        <Row label="Conversations" value={String(d.conversationsTotal)} />
        <Row
          label="LLM calls"
          value={String(d.llmCallsTotal)}
          hint="assistant turns that actually called Anthropic"
        />
        <Row
          label="Metered turns"
          value={String(d.meteredResponsesTotal)}
          hint="billing meter — excludes cap-exempt daily turns"
        />
        <Row
          label="Library-served"
          value={String(d.libraryServedTotal)}
          hint="served from the spot library, $0"
        />
      </Section>

      <Section title="LLM cost">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <BigStat label="Today" value={formatUsd(d.costToday)} />
          <BigStat label="This month" value={formatUsd(d.costThisMonth)} />
          <BigStat label="All time" value={formatUsd(d.costAllTime.totalCost)} />
        </div>
        <Row
          label="Avg / LLM call"
          value={formatUsd(d.avgCostPerLlmCall)}
        />
        <div className="mt-3 rounded-lg border border-border overflow-hidden">
          <CostRow
            label="Input"
            tokens={d.costAllTime.inputTokens}
            cost={d.costAllTime.inputCost}
          />
          <CostRow
            label="Output"
            tokens={d.costAllTime.outputTokens}
            cost={d.costAllTime.outputCost}
          />
          <CostRow
            label="Cache write"
            tokens={d.costAllTime.cacheWriteTokens}
            cost={d.costAllTime.cacheWriteCost}
          />
          <CostRow
            label="Cache read"
            tokens={d.costAllTime.cacheReadTokens}
            cost={d.costAllTime.cacheReadCost}
          />
        </div>
        <p className="text-[11px] text-text-muted/70 mt-2">
          Opus 4.8 rates · derived from stored token usage · active
          conversations only.
        </p>
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <div className="text-text-muted">
        {label}
        {hint && <div className="text-[11px] text-text-muted/60">{hint}</div>}
      </div>
      <div className="font-mono tabular-nums text-text text-right">{value}</div>
    </div>
  );
}

function BigStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-3 text-center">
      <div className="text-lg font-bold text-text tabular-nums">{value}</div>
      <div className="text-[11px] text-text-muted mt-0.5">{label}</div>
    </div>
  );
}

function CostRow({
  label,
  tokens,
  cost,
}: {
  label: string;
  tokens: number;
  cost: number;
}) {
  return (
    <div className="flex items-center justify-between px-3 py-2 text-sm border-b border-border/40 last:border-0">
      <span className="text-text-muted">{label}</span>
      <span className="flex gap-4 font-mono tabular-nums">
        <span className="text-text-muted/70">{formatTokens(tokens)}</span>
        <span className="text-text w-16 text-right">{formatUsd(cost)}</span>
      </span>
    </div>
  );
}
